import { create } from "zustand";
import { QueryClientParams } from "~/services/client/client.types";

export enum ActiveTabClient {
  INFO = "info",
  RATES = "rates",
  DOCUMENTS = "documents",
  PREFERENCES = "preferences",
}

type TempClientData = {
  name?: string;
  email?: string;
  phone?: string;
};

type State = {
  formState: {
    open: boolean;
    clientId?: string;
    activeTab: ActiveTabClient;
    prefillData?: TempClientData;
    bookingIdToLink?: string; // After creating client, link to this booking
  };
  profileModal: {
    open: boolean;
    clientId?: string;
  };
  filters: QueryClientParams;
  formData: Record<ActiveTabClient, any>;
};

type Actions = {
  setFilters: (filters: QueryClientParams) => void;
  resetFilters: () => void;
  openClientModal: (clientId?: string, options?: { prefillData?: TempClientData; bookingIdToLink?: string }) => void;
  closeClientModal: () => void;
  setActiveTab: (tab: ActiveTabClient) => void;
  updateFormData: (tab: ActiveTabClient, data: any) => void;
  resetFormData: () => void;
  openProfileModal: (clientId: string) => void;
  closeProfileModal: () => void;
};

const initialState: State = {
  formState: {
    open: false,
    activeTab: ActiveTabClient.INFO,
  },
  profileModal: {
    open: false,
  },
  filters: {
    page: 1,
    pageSize: 10,
    search: "",
  },
  formData: {
    [ActiveTabClient.INFO]: {
      shippingAddresses: [{}],
    },
    [ActiveTabClient.RATES]: {},
    [ActiveTabClient.DOCUMENTS]: {},
    [ActiveTabClient.PREFERENCES]: {},
  },
};

export const useClientStore = create<State & Actions>()((set) => ({
  ...initialState,
  setFilters: (filters: QueryClientParams) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...filters,
      },
    }));
  },
  resetFilters: () => set({ filters: initialState.filters }),
  openClientModal: (clientId, options) =>
    set({
      formState: {
        open: true,
        clientId,
        activeTab: ActiveTabClient.INFO,
        prefillData: options?.prefillData,
        bookingIdToLink: options?.bookingIdToLink,
      },
    }),
  closeClientModal: () =>
    set({
      formState: {
        open: false,
        clientId: undefined,
        activeTab: ActiveTabClient.INFO,
        prefillData: undefined,
        bookingIdToLink: undefined,
      },
      formData: initialState.formData,
    }),
  setActiveTab: (tab: ActiveTabClient) =>
    set((state) => ({
      formState: {
        ...state.formState,
        activeTab: tab,
      },
    })),
  updateFormData: (tab: ActiveTabClient, data: any) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [tab]: { ...state.formData[tab], ...data },
      },
    })),
  resetFormData: () => set({ formData: initialState.formData }),
  openProfileModal: (clientId: string) =>
    set({
      profileModal: {
        open: true,
        clientId,
      },
    }),
  closeProfileModal: () =>
    set({
      profileModal: {
        open: false,
        clientId: undefined,
      },
    }),
}));
